import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { BrandPresetId, PlatformBranding, UpdatePlatformBrandingRequest } from '@workspace/types';
import { brandingApi } from '../../features/platform-admin/api/brandingApi';
import { applyBrandThemeToDocument } from '../../themes/apply-brand-theme';
import { BRAND_PRESETS } from '../../themes/presets';
import { resolveBrandConfig, type ResolvedBrandConfig } from '../../themes/resolve-brand-config';
import { getEffectiveTheme, useTheme } from './theme-provider.utils';
import { BrandThemeContext } from './brand-theme-provider.utils';

function toResolvedConfig(branding: PlatformBranding | null): ResolvedBrandConfig {
  return resolveBrandConfig(branding ?? undefined);
}

export function BrandThemeProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<PlatformBranding | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const { theme } = useTheme();
  const effectiveTheme = getEffectiveTheme(theme);

  const config = useMemo(() => toResolvedConfig(branding), [branding]);

  useEffect(() => {
    let cancelled = false;

    brandingApi
      .getPublicBranding()
      .then(data => {
        if (!cancelled) {
          setBranding(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBranding(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    applyBrandThemeToDocument(config, { dark: effectiveTheme === 'dark' });
    document.title = config.brandName;
  }, [config, effectiveTheme]);

  const setBrandPreset = useCallback(async (presetId: BrandPresetId) => {
    const previous = branding;
    const optimistic: PlatformBranding = {
      presetId,
      brandName: BRAND_PRESETS[presetId].name,
      logoUrl: previous?.logoUrl,
    };
    setBranding(optimistic);
    setSaving(true);

    try {
      const payload: UpdatePlatformBrandingRequest = { presetId };
      const saved = await brandingApi.updateBranding(payload);
      setBranding(saved);
    } catch (error) {
      setBranding(previous);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [branding]);

  const value = useMemo(
    () => ({
      presetId: config.presetId,
      brandName: config.brandName,
      logoUrl: config.logoUrl,
      loaded,
      saving,
      setBrandPreset,
    }),
    [config, loaded, saving, setBrandPreset],
  );

  return (
    <BrandThemeContext.Provider value={value}>{children}</BrandThemeContext.Provider>
  );
}
